import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { Tooltip } from 'aui';
import clsx from 'clsx';
import { debounce } from 'lodash-es';
import type React from 'react';
import { useEffect, useState } from 'react';

type VariableTagProps = {
  variableTag: string;
  variableName: string;
  variableValue?: string;
  className: React.HTMLAttributes<HTMLSpanElement>['className'];
  onClick?: (e: React.MouseEvent<HTMLSpanElement>) => void;
  clickable?: boolean;
};

const VariableTag: React.FC<VariableTagProps> = ({
  variableTag,
  variableName,
  variableValue,
  className,
  onClick,
  clickable = true,
}) => {
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);

  const [textToShow, setTextToShow] = useState(variableTag);

  useEffect(() => {
    if (variableValue) {
      setTextToShow(variableValue);
    } else {
      setTextToShow(variableTag);
    }
  }, [variableValue]);

  const handleMouseOver = debounce(() => {
    if (variableValue) {
      setTextToShow(variableTag);
    }
  }, 200);

  const handleMouseLeave = debounce(() => {
    if (variableValue) {
      setTextToShow(variableValue);
    }
  }, 200);

  const node = (
    <span
      className={clsx(
        'rounded-md border bg-primary-1 px-1 py-px text-sm font-medium transition-colors',
        clickable && 'cursor-pointer hover:bg-primary-2',
        className,
      )}
      onClick={(e) => {
        if (!clickable) return;
        onClick?.(e);
      }}
      onMouseOver={() => {
        handleMouseOver();
      }}
      onMouseLeave={() => {
        handleMouseLeave();
      }}
      onFocus={() => {
        handleMouseOver();
      }}
    >
      {textToShow}
    </span>
  );

  if (clickable) {
    return (
      <Tooltip
        title={clientI18n('environment.manageModal.tip.clickToSetVariable', {
          variableName,
          variableValue: variableValue ?? clientI18n('environment.variable.notSet'),
        })}
      >
        {node}
      </Tooltip>
    );
  }

  return node;
};

export default VariableTag;
